import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';

import { RequiredBy } from '@utils/typeUtils';

import { TextField, TextFieldProps } from './TextField';

export type TextFieldWithDebounceProps = RequiredBy<TextFieldProps, 'onChange'> & {
  delay?: number;
};

export const TextFieldWithDebounce: FC<TextFieldWithDebounceProps> = ({
  delay = 300,
  value,
  onChange,
  ...props
}) => {
  const [localValue, setLocalValue] = useState<string>((value as string) ?? '');
  const timerRef = useRef<null | number>(null);

  useEffect(() => {
    if (value !== localValue) setLocalValue((value as string) ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const debounced = useCallback(
    (val: string, currentTarget: HTMLInputElement | HTMLTextAreaElement) => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = window.setTimeout(() => {
        const syntheticEvent = {
          target: { value: val },
          currentTarget,
        } as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

        onChange(syntheticEvent);
      }, delay);
    },
    [onChange, delay]
  );

  return (
    <TextField
      {...props}
      onChange={e => {
        setLocalValue(e.target.value);
        debounced(e.target.value, e.currentTarget);
      }}
      value={localValue}
    />
  );
};
